import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	ContactBlacklistedProspect,
	ContactProspect,
	ContactSettings,
} from "../../pages/Contacts/schema/contacts";
import { ContactProspectDetail } from "../../pages/Contacts/schema/contact-detail";
import { CrmProspectUpdate } from "../crm/crm.schema";
import { applyCrmUpdateToProspect } from "../crm/crmRealtimeUpdate";
import { contactApi } from "./contact.api";

type DeepResearchResponseData = NonNullable<ContactProspect["deepResearchResponse"]>["response"];

interface UpdateDeepResearchResponsePayload {
	prospectId: number;
	deepResearchResponseId: number;
	response: DeepResearchResponseData;
	fitAssessment?: string | null;
	fitReason?: string | null;
}

const buildDeepResearchResponse = (payload: UpdateDeepResearchResponsePayload) => ({
	id: payload.deepResearchResponseId,
	response: payload.response,
	fitAssessment: payload.fitAssessment ?? null,
	fitReason: payload.fitReason ?? null,
});

interface PaginationMeta {
	page: number;
	limit: number;
	itemCount: number;
	pageCount: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
	/**
	 * `itemCount` is the server's count cap, not the real total — there are more rows than it
	 * reports. Render it via `formatCappedCount` so it reads "N+" (PREC-3953).
	 */
	isCapped?: boolean;
}

interface ContactState {
	contacts: ContactProspect[];
	contactsMeta: PaginationMeta;
	blacklistedContacts: ContactBlacklistedProspect[];
	blacklistedContactsMeta: PaginationMeta;
	settings: ContactSettings | null;
	leadPanelProspectId: number | null;
	leadPanelProspectPreview: ContactProspect | null;
	leadPanelDetail: ContactProspectDetail | null;
	isLeadPanelDetailLoading: boolean;
	leadPanelDetailError: string | null;
	isLeadPanelOpen: boolean;
	/**
	 * Every prospect detail fetched this session, keyed by prospect id. Unlike
	 * `leadPanelDetail` (scoped to the open panel), this map feeds surfaces that
	 * show several prospect identity cards at once (the feed run cards).
	 */
	prospectDetailsById: Record<number, ContactProspectDetail>;
}

const defaultMeta: PaginationMeta = {
	page: 1,
	limit: 15,
	itemCount: 0,
	pageCount: 1,
	hasPreviousPage: false,
	hasNextPage: false,
};

const initialState: ContactState = {
	contacts: [],
	contactsMeta: { ...defaultMeta },
	blacklistedContacts: [],
	blacklistedContactsMeta: { ...defaultMeta },
	settings: null,
	leadPanelProspectId: null,
	leadPanelProspectPreview: null,
	leadPanelDetail: null,
	isLeadPanelDetailLoading: false,
	leadPanelDetailError: null,
	isLeadPanelOpen: false,
	prospectDetailsById: {},
};

export const contactSlice = createSlice({
	name: "contact",
	initialState,
	reducers: {
		addContactEmailData: (state, { payload }) => {
			// Enrichment arrives via socket after a search kicks off. Apply it to the table row, the
			// open lead panel (preview + detail) *and* the feed identity-card map (`prospectDetailsById`)
			// so every surface that shows this prospect reflects the result live.
			const applyEnrichment = (prospect: ContactProspect | ContactProspectDetail | null): void => {
				if (!prospect || prospect.id !== payload.prospectId) {
					return;
				}

				if (payload.emailStatus) {
					prospect.email = payload.email;
					prospect.emailStatus = payload.emailStatus;
				} else if (payload.phoneStatus) {
					prospect.phone = payload.phone;
					prospect.phoneStatus = payload.phoneStatus;
				}
			};

			const index = state.contacts.findIndex((p) => p.id === payload.prospectId);

			if (index !== -1) {
				applyEnrichment(state.contacts[index]);
				state.contacts[index] = state.contacts[index];
			}

			applyEnrichment(state.leadPanelProspectPreview);
			applyEnrichment(state.leadPanelDetail);
			applyEnrichment(state.prospectDetailsById[payload.prospectId] ?? null);
		},
		updateContactOrganizationContact: (
			state,
			{
				payload,
			}: {
				payload: {
					organizationId: number;
					email: string | null;
					phone: string | null;
				};
			}
		) => {
			for (let i = 0; i < state.contacts.length; i++) {
				if (state.contacts[i].organization?.id !== payload.organizationId) {
					continue;
				}

				if (payload.email !== null) {
					state.contacts[i].organization!.email = payload.email;
				}

				if (payload.phone !== null) {
					state.contacts[i].organization!.phone = payload.phone;
				}
			}
		},
		applyContactCrmProspectUpdate: (state, { payload }: PayloadAction<CrmProspectUpdate>) => {
			for (const contact of state.contacts) {
				applyCrmUpdateToProspect(contact, payload);
			}

			applyCrmUpdateToProspect(state.leadPanelProspectPreview, payload);
			applyCrmUpdateToProspect(state.leadPanelDetail, payload);
		},
		clearContacts: (state) => {
			state.contacts = [];
		},
		openContactLeadPanel: (state, { payload }: { payload: ContactProspect }) => {
			state.leadPanelProspectId = payload.id;
			state.leadPanelProspectPreview = payload;
			state.leadPanelDetail = null;
			state.leadPanelDetailError = null;
			state.isLeadPanelDetailLoading = false;
			state.isLeadPanelOpen = true;
		},
		/**
		 * Directly hydrate the lead panel detail from an externally fetched
		 * source (e.g. the Chrome extension, which resolves the detail via the
		 * `prospect/by-linkedin-url` endpoint instead of `getContactProspectDetail`).
		 */
		setLeadPanelDetail: (state, { payload }: { payload: ContactProspectDetail }) => {
			state.leadPanelDetail = payload;
			state.leadPanelProspectId = payload.id;
			state.isLeadPanelDetailLoading = false;
			state.leadPanelDetailError = null;
		},
		closeContactLeadPanel: (state) => {
			state.leadPanelProspectId = null;
			state.leadPanelProspectPreview = null;
			state.leadPanelDetail = null;
			state.leadPanelDetailError = null;
			state.isLeadPanelDetailLoading = false;
			state.isLeadPanelOpen = false;
		},
		updateContactProspectGender: (
			state,
			{
				payload,
			}: {
				payload: {
					prospectId: number;
					gender: string | null;
				};
			}
		) => {
			const index = state.contacts.findIndex((p) => p.id === payload.prospectId);

			if (index === -1) {
				return;
			}

			state.contacts[index].gender = payload.gender;
			state.contacts[index].genderProbability = payload.gender ? 100 : null;
		},
		updateDeepResearchResponseInContacts: (
			state,
			{ payload }: PayloadAction<UpdateDeepResearchResponsePayload>
		) => {
			const deepResearchResponse = buildDeepResearchResponse(payload);
			const contact = state.contacts.find((person) => person.id === payload.prospectId);

			if (contact) {
				contact.deepResearchResponse = deepResearchResponse;
			}

			if (state.leadPanelProspectPreview?.id === payload.prospectId) {
				state.leadPanelProspectPreview.deepResearchResponse = deepResearchResponse;
			}

			if (state.leadPanelDetail?.id === payload.prospectId) {
				state.leadPanelDetail.deepResearchResponse = deepResearchResponse;
			}
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			contactApi.endpoints.getContactProspectDetail.matchPending,
			(state, action) => {
				if (state.leadPanelProspectId !== action.meta.arg.originalArgs) {
					return;
				}

				state.isLeadPanelDetailLoading = true;
				state.leadPanelDetailError = null;
			}
		);

		builder.addMatcher(
			contactApi.endpoints.getContactProspectDetail.matchFulfilled,
			(state, { meta, payload }) => {
				// Every fetched detail lands in the by-id map (feed identity cards);
				// the panel fields below stay scoped to the panel's own prospect.
				state.prospectDetailsById[payload.id] = payload;

				if (state.leadPanelProspectId !== meta.arg.originalArgs) {
					return;
				}

				state.leadPanelDetail = payload;
				state.isLeadPanelDetailLoading = false;
				state.leadPanelDetailError = null;
			}
		);

		builder.addMatcher(
			contactApi.endpoints.getContactProspectDetail.matchRejected,
			(state, action) => {
				if (state.leadPanelProspectId !== action.meta.arg.originalArgs) {
					return;
				}

				state.isLeadPanelDetailLoading = false;
				state.leadPanelDetailError = "contacts.leadPanel.loadError";
			}
		);

		builder.addMatcher(contactApi.endpoints.getContacts.matchFulfilled, (state, { payload }) => {
			if (!payload) {
				return;
			}

			if (payload.data) {
				state.contacts = payload.data;
			}

			if (payload.meta) {
				state.contactsMeta = payload.meta;
			}
		});

		builder.addMatcher(
			contactApi.endpoints.getBlacklistedContacts.matchFulfilled,
			(state, { payload }) => {
				if (!payload) {
					return;
				}

				if (payload.data) {
					state.blacklistedContacts = payload.data;
				}

				if (payload.meta) {
					state.blacklistedContactsMeta = payload.meta;
				}
			}
		);

		builder.addMatcher(
			contactApi.endpoints.contactSearchForEmails.matchFulfilled,
			(state, action) => {
				for (const person of action.payload) {
					// The search response carries the initial status (e.g. SEARCHING); reflect it on the
					// table row, the open lead panel and the feed identity-card map so the email icon flips
					// to the spinner immediately on every surface.
					const applyStatus = (prospect: ContactProspect | ContactProspectDetail | null): void => {
						if (prospect && prospect.id === person.id) {
							prospect.emailStatus = person.emailStatus;
						}
					};

					const index = state.contacts.findIndex((p) => p.id === person.id);

					if (index !== -1) {
						state.contacts[index].emailStatus = person.emailStatus;
						state.contacts[index] = state.contacts[index];
					}

					applyStatus(state.leadPanelProspectPreview);
					applyStatus(state.leadPanelDetail);
					applyStatus(state.prospectDetailsById[person.id] ?? null);
				}
			}
		);

		builder.addMatcher(
			contactApi.endpoints.contactSearchForPhones.matchFulfilled,
			(state, action) => {
				for (const person of action.payload) {
					// The search response carries the initial status (e.g. SEARCHING); reflect it on the
					// table row, the open lead panel and the feed identity-card map so the phone icon flips
					// to the spinner immediately on every surface.
					const applyStatus = (prospect: ContactProspect | ContactProspectDetail | null): void => {
						if (prospect && prospect.id === person.id) {
							prospect.phoneStatus = person.phoneStatus;
						}
					};

					const index = state.contacts.findIndex((p) => p.id === person.id);

					if (index !== -1) {
						state.contacts[index].phoneStatus = person.phoneStatus;
						state.contacts[index] = state.contacts[index];
					}

					applyStatus(state.leadPanelProspectPreview);
					applyStatus(state.leadPanelDetail);
					applyStatus(state.prospectDetailsById[person.id] ?? null);
				}
			}
		);

		builder.addMatcher(
			contactApi.endpoints.getContactSettings.matchFulfilled,
			(state, { payload }) => {
				state.settings = payload;
			}
		);

		builder.addMatcher(
			contactApi.endpoints.updateContactSettings.matchFulfilled,
			(state, { payload }) => {
				state.settings = payload;
			}
		);
	},
});

export const {
	addContactEmailData,
	applyContactCrmProspectUpdate,
	updateContactOrganizationContact,
	clearContacts,
	openContactLeadPanel,
	setLeadPanelDetail,
	closeContactLeadPanel,
	updateContactProspectGender,
	updateDeepResearchResponseInContacts,
} = contactSlice.actions;
export const contactReducer = contactSlice.reducer;
export const contactSelector = (state: RootState) => state[contactSlice.name];
